import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { ButtonLinkComponent, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { CALENDAR_WEEKDAYS, CalendarCell, CalendarDate, CalendarMonth, CalendarRange, MiniCalendarMode, applyRangeSelection, buildMonthCells, firstMonthFor, formatMonthLabel, isMonthDisplayed, isWithinRange, monthSequence, parseCalendarDate, previewRangeSelection, shiftDay, shiftMonth, todayCalendarDate, weekdayColumn } from './mini.calendar.model';
import { miniCalendarBridgeStyles, miniCalendarStyleId } from './mini.calendar.theme';

/** One rendered panel: its heading and its 7-column grid. */
interface CalendarPanel {
  readonly label: string;
  readonly cells: readonly CalendarCell[];
}

/**
 * A compact month grid for picking one date or a range — the body of a skip or extra-visit popover,
 * and, at `months = 2`, the two-up range picker the blackout dialog opens on.
 *
 * Dates cross the boundary only as `YYYY-MM-DD` strings, never as `Date`. A `Date` carries a zone and a
 * time-of-day, and both of them turn a picked day into the day before for half the world; the maths
 * lives in `mini.calendar.model.ts` and constructs its `Date`s from numeric parts so no parsing is
 * involved. Weeks always start Monday, which is why the grid cannot use `getDay()` directly.
 *
 * The displayed month follows the selection's month, but a chevron press pins it until the selection
 * moves to a month outside the displayed window. Pinning matters: a range whose second click lands in
 * the next month would otherwise yank the grid out from under the user mid-selection.
 *
 * Keyboard: the grid is a single tab stop (roving `tabindex`), arrows move by day and week, Home/End
 * to the ends of the week, PageUp/PageDown by month, and Enter or Space picks. Moving off the
 * displayed window turns the page rather than refusing the move.
 */
@Component({
  selector: 'wb-mini-calendar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonLinkComponent],
  templateUrl: 'mini.calendar.component.html',
  styleUrls: ['./mini.calendar.component.less'],
})
export class MiniCalendarComponent {

  private readonly document = inject(DOCUMENT);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  public readonly $mode = input<MiniCalendarMode>(MiniCalendarMode.Single, { alias: 'mode' });

  /** How many month panels sit side by side. One prev/next pair drives the whole run. */
  public readonly $months = input<1 | 2>(1, { alias: 'months' });

  /**
   * Shows a `Today` shortcut in the header. Off by default so the single-month callers — the schedule
   * editor's four pickers and the exception popover — keep the header they already have.
   */
  public readonly $showToday = input(false, { alias: 'showToday' });

  /** Selected day in single mode. */
  public readonly $value = input<CalendarDate | null>(null, { alias: 'value' });

  /** Selected endpoints in range mode. */
  public readonly $range = input<CalendarRange>({}, { alias: 'range' });

  /** Days this predicate rejects render muted and refuse clicks. */
  public readonly $disabledDate = input<((date: CalendarDate) => boolean) | null>(null, { alias: 'disabledDate' });

  /** Emitted on every click in single mode. */
  public readonly valueChange = output<CalendarDate>();

  /** Emitted on every click in range mode, carrying the range after the click is applied. */
  public readonly rangeChange = output<CalendarRange>();

  protected readonly weekdays = CALENDAR_WEEKDAYS;

  protected readonly linkSize = ButtonSize.Small;

  protected readonly today = todayCalendarDate();

  /**
   * The day a pointer or the keyboard is resting on. It drives the prospective band while a range is
   * half-open, so the user can see the span before committing the second click.
   */
  private readonly $hovered = signal<CalendarDate | null>(null);

  /**
   * Month of the current selection — the grid's seed, and the only thing that may move it on its own.
   * Computed rather than read in a field initialiser: signal inputs are not bound yet at construction,
   * so seeding eagerly would always fall back to today and strand a bound value off-screen.
   */
  private readonly $selectedMonth = computed<CalendarMonth>(() => {

    const seed = parseCalendarDate(this.$value()) ?? parseCalendarDate(this.$range().from) ?? parseCalendarDate(this.today);

    return { year: seed.year, month: seed.month };
  });

  /**
   * The first month on screen. It follows the selection's month, but a chevron press overrides it and
   * that override survives every later change within the same month — otherwise a range whose second
   * click lands in the next month would yank the grid out from under the user mid-selection.
   */
  private readonly $displayed = linkedSignal<CalendarMonth, CalendarMonth>({
    source: () => this.$selectedMonth(),
    computation: (source, previous) => (previous && previous.source.year === source.year && previous.source.month === source.month ? previous.value : source),
  });

  protected readonly $panels = computed<readonly CalendarPanel[]>(() => monthSequence(this.$displayed(), this.$months()).map(month => ({ label: formatMonthLabel(month), cells: buildMonthCells(month.year, month.month) })));

  /**
   * The range as it would stand if the pending pick landed on the hovered day. With a closed range or
   * nothing started this is just the bound range, so the painted band never lies about what is stored.
   */
  private readonly $previewRange = computed<CalendarRange>(() => previewRangeSelection(this.$range(), this.$hovered()));

  /**
   * The one day in the grid that holds the tab stop. It tracks the selection so Tab lands where the
   * user last was, and falls back to today, then to the first day of the first panel — a month the
   * user has paged to that contains neither still needs somewhere for focus to enter.
   */
  private readonly $focusedDay = signal<CalendarDate | null>(null);

  protected readonly $tabStop = computed<CalendarDate | null>(() => {

    const displayed = this.$displayed();
    const months = this.$months();
    const candidates = [this.$focusedDay(), this.$mode() === MiniCalendarMode.Range ? this.$range().from : this.$value(), this.today];
    const inWindow = candidates.find(candidate => candidate && isMonthDisplayed(candidate, displayed, months));
    if (inWindow) return inWindow;

    return this.$panels()[0].cells.find(cell => cell.date !== null)?.date ?? null;
  });

  /**
   * Whether the `Today` shortcut has anywhere to take the user. Today already on screen means the
   * button would be a no-op, and a control that does nothing is worse than one that is not there — so
   * it is withheld rather than disabled, keeping the header from carrying dead weight.
   */
  protected readonly $todayAvailable = computed(() => this.$showToday() && !isMonthDisplayed(this.today, this.$displayed(), this.$months()));

  public constructor() {

    DesignSystem.Mount(this.document, miniCalendarStyleId, miniCalendarBridgeStyles);
  }

  protected onShiftMonth(delta: number): void {

    this.$displayed.update(current => shiftMonth(current, delta));
  }

  /**
   * Moves the view so today's month is the first panel and puts the roving focus on today. It is a
   * navigation control, not a pick: nothing is emitted, so a half-open range survives the jump and the
   * user can still close it on a day they arrived at by scrolling months away.
   */
  protected onToday(): void {

    const parts = parseCalendarDate(this.today);
    if (!parts) return;

    this.$displayed.set({ year: parts.year, month: parts.month });
    this.$focusedDay.set(this.today);
    queueMicrotask(() => (this.host.nativeElement.querySelector(`.calendar-day[data-date="${this.today}"]`) as HTMLElement | null)?.focus());
  }

  protected onSelect(cell: CalendarCell): void {

    const date = cell.date;
    if (!date || this.isDisabled(date)) return;

    this.$focusedDay.set(date);

    if (this.$mode() === MiniCalendarMode.Range) {
      this.rangeChange.emit(applyRangeSelection(this.$range(), date));
      return;
    }

    this.valueChange.emit(date);
  }

  protected onHover(date: CalendarDate | null): void {

    this.$hovered.set(date && !this.isDisabled(date) ? date : null);
  }

  /**
   * Arrow keys move by day and week, Home/End to the ends of the week, PageUp/PageDown by month, and
   * Enter or Space picks the focused day. A move that leaves the displayed window turns the page, so a
   * keyboard user is never stopped at a month boundary.
   */
  protected onKeydown(event: KeyboardEvent, date: CalendarDate): void {

    const delta = MiniCalendarComponent.DayDeltas[event.key];

    if (delta !== undefined) {
      event.preventDefault();
      this.moveFocus(shiftDay(date, delta));
      return;
    }

    // Home/End walk to the Monday and Sunday of the focused week — the column index is the offset.
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const column = weekdayColumn(date);
      this.moveFocus(shiftDay(date, event.key === 'Home' ? -column : 6 - column));
      return;
    }

    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      this.$displayed.update(current => shiftMonth(current, event.key === 'PageUp' ? -1 : 1));
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSelect({ date, day: parseCalendarDate(date)?.day ?? null });
    }
  }

  /** Focus follows the roving tab stop, so a click or a Tab into the grid sets where the arrows start. */
  protected onDayFocus(date: CalendarDate): void {

    this.$focusedDay.set(date);
    this.onHover(date);
  }

  protected onDayBlur(): void {

    this.$hovered.set(null);
  }

  protected isTabStop(date: CalendarDate | null): boolean {

    return date !== null && date === this.$tabStop();
  }

  protected isDisabled(date: CalendarDate | null): boolean {

    if (!date) return false;

    const predicate = this.$disabledDate();

    return predicate ? predicate(date) : false;
  }

  /** True for an endpoint of the range, or for the single selected day — both paint solid brand. */
  protected isSelected(date: CalendarDate | null): boolean {

    if (!date) return false;

    if (this.$mode() === MiniCalendarMode.Range) {
      const range = this.$range();
      return date === range.from || date === range.to;
    }

    return date === this.$value();
  }

  /** Strictly between the committed endpoints — the tinted band, never an endpoint. */
  protected isInRange(date: CalendarDate | null): boolean {

    if (!date || this.$mode() !== MiniCalendarMode.Range) return false;

    return isWithinRange(date, this.$range());
  }

  /** Inside the band the pending second click would create, and not already part of the committed range. */
  protected isPreviewRange(date: CalendarDate | null): boolean {

    if (!date || this.$mode() !== MiniCalendarMode.Range || this.isSelected(date)) return false;

    return isWithinRange(date, this.$previewRange());
  }

  /** The prospective far endpoint while the second click is pending — outlined, not solid. */
  protected isPreviewEnd(date: CalendarDate | null): boolean {

    if (!date || this.$mode() !== MiniCalendarMode.Range || this.isSelected(date)) return false;

    const preview = this.$previewRange();

    return Boolean(preview.to) && (date === preview.from || date === preview.to);
  }

  protected isToday(date: CalendarDate | null): boolean {

    return date !== null && date === this.today;
  }

  /**
   * Steps focus to `date`, paging the displayed window when the move leaves it. The lookup is scoped to
   * this host: a page can hold several calendars (the schedule editor holds four), and a document-wide
   * query would hand focus to whichever one happened to render that day first.
   */
  private moveFocus(date: CalendarDate): void {

    this.$displayed.update(current => firstMonthFor(date, current, this.$months()));
    this.$focusedDay.set(date);
    this.onHover(date);
    queueMicrotask(() => (this.host.nativeElement.querySelector(`.calendar-day[data-date="${date}"]`) as HTMLElement | null)?.focus());
  }

  private static readonly DayDeltas: Readonly<Record<string, number>> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
}
